import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
	db,
	getAdjacentCallId,
	getReleasedCallRecordingUrl,
	isEmptyCall,
	type OutboundCallContext,
	type OutboundPlaybook,
	type OutboundTask,
	type OutboundTaskAttempt,
	outboundPlaybooksCollection,
	outboundTaskAttemptsCollection,
	outboundTasksCollection,
	resolveOutboundCallContext,
} from "@repo/ui";
import * as Sentry from "@sentry/react";
import { onSnapshot } from "firebase/firestore";
import { AlertTriangle, Calendar, Filter, Phone, Search } from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { useCallListActions } from "../hooks/useCallListActions";
import { search as searchIndex } from "../search/searchIndex";
import type { Call } from "../types";
import { isAdminRole } from "../utils/auth";
import {
	callPageBounds,
	defaultCallsRange,
	formatCallsCount,
	isWithinCallsRange,
	recentCallsRange,
} from "../utils/callsView";
import { formatCaseId } from "../utils/formatCaseId";
import { normalizePhone } from "../utils/normalizePhone";
import { parseDate } from "../utils/parseDate";
import { getContentFieldProps } from "../utils/textDirection";
import { CallDetails } from "./CallDetails";
import { CallRecord } from "./CallRecord";
import { ContentState } from "./ContentState";
import { PageHeader } from "./PageHeader";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Calendar as CalendarComponent } from "./ui/calendar";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
} from "./ui/pagination";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import { Skeleton } from "./ui/skeleton";

interface CallsPageProps {
	selectedCallId?: string | null;
	onCallSelect?: (callId: string | null) => void;
}

export function CallsPage({
	selectedCallId,
	onCallSelect,
}: CallsPageProps = {}) {
	const { calls, callsLoading, callsError, agents, user, language } = useData();
	const { i18n } = useLingui();
	const { markRead, toggleArchive } = useCallListActions(user?.companyId);
	const audioRef = useRef<HTMLAudioElement>(null);
	const [playbackRate, setPlaybackRate] = useState(1);
	// selectedCall is now derived from props
	const selectedCall = selectedCallId
		? calls.find((c) => c.id === selectedCallId && !c.piiRemovalPending)
		: null;
	const [searchQuery, setSearchQuery] = useState("");
	const [currentPage, setCurrentPage] = useState(1);
	const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>(
		defaultCallsRange,
	);
	const [datePickerOpen, setDatePickerOpen] = useState(false);
	const [draftDateRange, setDraftDateRange] = useState(dateRange);
	const [hideArchived, setHideArchived] = useState(true);
	const [hideTestCalls, setHideTestCalls] = useState(false);
	const [hideBlockedCalls, setHideBlockedCalls] = useState(false);
	const [hideEmptyCalls, setHideEmptyCalls] = useState(true);
	const [directionFilter, setDirectionFilter] = useState<
		"any" | "inbound" | "outbound" | "unknown"
	>("any");
	const [playbookFilter, setPlaybookFilter] = useState("all");
	const [outboundPlaybooks, setOutboundPlaybooks] = useState<
		OutboundPlaybook[]
	>([]);
	const [outboundTasks, setOutboundTasks] = useState<OutboundTask[]>([]);
	const [outboundAttempts, setOutboundAttempts] = useState<
		OutboundTaskAttempt[]
	>([]);

	const [audioUrl, setAudioUrl] = useState<string | null>(null);
	const itemsPerPage = 10;

	const isAdmin = isAdminRole(user?.role);

	useEffect(() => {
		if (!user?.companyId) {
			setOutboundPlaybooks([]);
			setOutboundTasks([]);
			setOutboundAttempts([]);
			return;
		}

		const unsubscribePlaybooks = onSnapshot(
			outboundPlaybooksCollection(db, user.companyId),
			(snapshot) =>
				setOutboundPlaybooks(snapshot.docs.map((doc) => doc.data())),
			(error) => {
				console.error("Failed to load outbound playbooks for calls", error);
				Sentry.captureException(error);
			},
		);
		const unsubscribeTasks = onSnapshot(
			outboundTasksCollection(db, user.companyId),
			(snapshot) => setOutboundTasks(snapshot.docs.map((doc) => doc.data())),
			(error) => {
				console.error("Failed to load outbound tasks for calls", error);
				Sentry.captureException(error);
			},
		);
		const unsubscribeAttempts = onSnapshot(
			outboundTaskAttemptsCollection(db, user.companyId),
			(snapshot) => setOutboundAttempts(snapshot.docs.map((doc) => doc.data())),
			(error) => {
				console.error("Failed to load outbound task attempts for calls", error);
				Sentry.captureException(error);
			},
		);

		return () => {
			unsubscribePlaybooks();
			unsubscribeTasks();
			unsubscribeAttempts();
		};
	}, [user?.companyId]);

	const outboundContextByCallId = useMemo(() => {
		const contexts = new Map<string, OutboundCallContext>();
		for (const call of calls) {
			const context = resolveOutboundCallContext({
				call,
				playbooks: outboundPlaybooks,
				tasks: outboundTasks,
				attempts: outboundAttempts,
			});
			if (context.isOutbound) contexts.set(call.id, context);
		}
		return contexts;
	}, [calls, outboundPlaybooks, outboundTasks, outboundAttempts]);

	const playbookFilterOptions = useMemo(() => {
		const options = new Map<string, string>();
		for (const playbook of outboundPlaybooks) {
			if (playbook.id) options.set(playbook.id, playbook.name || playbook.id);
		}
		for (const context of outboundContextByCallId.values()) {
			if (context.playbookId) {
				options.set(
					context.playbookId,
					context.playbookName || context.playbookId,
				);
			}
		}
		return Array.from(options, ([id, name]) => ({ id, name })).sort((a, b) =>
			a.name.localeCompare(b.name),
		);
	}, [outboundPlaybooks, outboundContextByCallId]);

	// Get agent's company
	const getAgentCompany = (agentName: string): string | undefined => {
		const agent = agents.find((a) => a.name === agentName);
		return agent?.companyId;
	};

	const matchesBasicSearch = (call: Call) => {
		const normalizedQuery = searchQuery.toLowerCase();
		const nameMatch = call.caller?.name
			?.toLowerCase()
			.includes(normalizedQuery);
		const phoneMatch = call.caller?.phone?.includes(searchQuery);
		const agentMatch = call.agent?.toLowerCase().includes(normalizedQuery);

		let caseIdMatch = false;
		if (call.caseId != null) {
			const cleanQuery = searchQuery.trim().replace(/^#/, "");
			if (/^\d+$/.test(cleanQuery)) {
				caseIdMatch =
					String(call.caseId) === String(Number(cleanQuery)) ||
					formatCaseId(call.caseId).includes(cleanQuery);
			}
		}

		return Boolean(nameMatch || phoneMatch || agentMatch || caseIdMatch);
	};

	const searchMatchedCallIds = useMemo(() => {
		if (!searchQuery.trim()) {
			return null;
		}

		const results = searchIndex(searchQuery);
		const matchedCallIds = new Set<string>();
		const matchedCallerIds = new Set<string>();
		const matchedActionItemIds = new Set<string>();

		results.forEach((result) => {
			if (result.type === "call") {
				matchedCallIds.add(result.id);
			} else if (result.type === "caller") {
				matchedCallerIds.add(result.id);
			} else if (result.type === "actionItem") {
				matchedActionItemIds.add(result.id);
			}
		});

		if (matchedCallerIds.size || matchedActionItemIds.size) {
			calls.forEach((call) => {
				const callerMatch = matchedCallerIds.has(
					normalizePhone(call.caller.phone),
				);
				const actionItemMatch =
					call.actionItems?.some((item) =>
						matchedActionItemIds.has(item.id!),
					) ?? false;

				if (callerMatch || actionItemMatch) {
					matchedCallIds.add(call.id);
				}
			});
		}

		return matchedCallIds;
	}, [calls, searchQuery]);

	// Filter calls by search query, date range, and company (for admins)
	const filteredCalls = calls.filter((call) => {
		if (hideEmptyCalls && !call.piiRemovalPending && isEmptyCall(call))
			return false;

		const matchesSearch =
			!searchQuery.trim() ||
			matchesBasicSearch(call) ||
			(searchMatchedCallIds?.has(call.id) ?? false);

		if (!matchesSearch) return false;

		const outboundContext = outboundContextByCallId.get(call.id);
		const isOutbound = Boolean(outboundContext?.isOutbound);
		if (directionFilter === "inbound" && call.direction !== "inbound")
			return false;
		if (directionFilter === "outbound" && call.direction !== "outbound")
			return false;
		if (directionFilter === "unknown" && call.direction != null) return false;
		if (
			playbookFilter !== "all" &&
			(!isOutbound || outboundContext?.playbookId !== playbookFilter)
		) {
			return false;
		}

		// Archive filtering
		if (hideArchived && call.interactionStatus === "archived") {
			return false;
		}

		// Test call filtering
		if (hideTestCalls && call.isTestCall) {
			return false;
		}

		// Blocked call filtering
		if (hideBlockedCalls && call.isBlocked) {
			return false;
		}

		return isWithinCallsRange(
			call.occurredAt ?? parseDate(call.date),
			dateRange,
		);
	});

	const compactDatePickerClassNames = {
		caption_label: "text-xs font-medium",
		head_cell: "text-muted-foreground rounded-md w-7 font-normal text-[0.7rem]",
		day: "size-7 p-0 text-xs font-normal aria-selected:opacity-100",
	};

	// Pagination logic
	const totalPages = Math.ceil(filteredCalls.length / itemsPerPage);
	const { page, startIndex, endIndex } = callPageBounds(
		currentPage,
		itemsPerPage,
		filteredCalls.length,
	);
	const paginatedCalls = filteredCalls.slice(startIndex, endIndex);
	const filteredCallIds = filteredCalls.map((call) => call.id);
	const previousCallId = selectedCallId
		? getAdjacentCallId(filteredCallIds, selectedCallId, "previous")
		: null;
	const nextCallId = selectedCallId
		? getAdjacentCallId(filteredCallIds, selectedCallId, "next")
		: null;

	const navigateToListedCall = (callId: string) => {
		const callIndex = filteredCallIds.indexOf(callId);
		if (callIndex !== -1) {
			setCurrentPage(Math.floor(callIndex / itemsPerPage) + 1);
		}
		if (calls.some((call) => call.id === callId && !call.piiRemovalPending))
			onCallSelect?.(callId);
	};

	useEffect(() => {
		if (currentPage !== page) setCurrentPage(page);
	}, [currentPage, page]);

	// Reset to page 1 when filters change
	// biome-ignore lint/correctness/useExhaustiveDependencies: Filter changes intentionally reset pagination even though the reset does not read their values.
	useEffect(() => {
		setCurrentPage(1);
	}, [
		searchQuery,
		dateRange,
		directionFilter,
		playbookFilter,
		hideEmptyCalls,
		hideArchived,
		hideTestCalls,
		hideBlockedCalls,
	]);

	// Format date for display
	const formatDateRange = (): string => {
		if (!dateRange.from && !dateRange.to)
			return t({ id: "selectDateRange", message: "Select date range" });
		if (dateRange.from && dateRange.to) {
			return `${dateRange.from.toLocaleDateString(language === "he" ? "he-IL" : "en-US")} - ${dateRange.to.toLocaleDateString(language === "he" ? "he-IL" : "en-US")}`;
		}
		if (dateRange.from) {
			return `${t({ id: "from", message: "From" })} ${dateRange.from.toLocaleDateString(language === "he" ? "he-IL" : "en-US")}`;
		}
		if (dateRange.to) {
			return `${t({ id: "to", message: "To" })} ${dateRange.to.toLocaleDateString(language === "he" ? "he-IL" : "en-US")}`;
		}
		return t({ id: "selectDateRange", message: "Select date range" });
	};
	const dateRangeLabel = formatDateRange();
	const defaultRange = defaultCallsRange();
	const periodLabel =
		dateRange.from?.getTime() === defaultRange.from.getTime() &&
		dateRange.to?.getTime() === defaultRange.to.getTime()
			? t({ id: "dashboard.last30Days", message: "Last 30 days" })
			: !dateRange.from && !dateRange.to
				? t({ id: "calls.allDates", message: "All dates" })
				: dateRangeLabel;

	const clearDateFilter = () => {
		setDateRange({});
	};

	const hideFilterCount = [
		hideEmptyCalls,
		hideArchived,
		hideTestCalls,
		hideBlockedCalls,
	].filter(Boolean).length;

	const clearAllFilters = () => {
		setSearchQuery("");
		setDateRange({});
		setDirectionFilter("any");
		setPlaybookFilter("all");
		setHideEmptyCalls(false);
		setHideArchived(false);
		setHideTestCalls(false);
		setHideBlockedCalls(false);
	};

	const handlePlayAudio = async (call: Call) => {
		if (!call.gcsUri) {
			toast.error(
				t({
					id: "recordingUnavailable",
					message: "No recording is available for this call.",
				}),
			);
			return;
		}

		setPlaybackRate(1);

		try {
			let url = call.gcsUri;
			if (url.startsWith("gs://")) {
				url = (
					await getReleasedCallRecordingUrl({
						companyId: user?.companyId ?? "",
						callSid: call.id,
					})
				).data.url;
			}
			setAudioUrl(url);
		} catch (error) {
			console.error(`Error getting audio URL for ${call.gcsUri}:`, error);
			Sentry.captureException(error);
			toast.error(
				t({
					id: "recordingLoadFailed",
					message: "The recording could not be loaded. Please try again.",
				}),
			);
		}
	};

	const handleToggleArchive = async (callId: string, status?: string) => {
		const archived = await toggleArchive(callId, status);
		if (archived && selectedCallId === callId) onCallSelect?.(null);
	};

	return (
		<div
			className="mx-auto max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8"
			data-testid="calls-page"
		>
			{selectedCall ? (
				<CallDetails
					call={selectedCall}
					onBack={() => {
						onCallSelect?.(null);
					}}
					onPlayAudio={() => handlePlayAudio(selectedCall)}
					onNavigateToCall={navigateToListedCall}
					previousCallId={previousCallId}
					nextCallId={nextCallId}
					shortcutsEnabled={!audioUrl}
					outboundContext={outboundContextByCallId.get(selectedCall.id)}
				/>
			) : (
				<>
					<PageHeader
						title={t({ id: "calls", message: "Calls" })}
						description={t({
							id: "callsPageSubtitle",
							message: "Manage and review agent interactions",
						})}
					/>

					{/* Search and Filters */}
					<Card>
						<CardContent className="p-4">
							<div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
								{/* Search Bar - Grows */}
								<div className="relative w-full md:min-w-0 md:flex-1">
									<Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										placeholder={t({
											id: "searchCalls",
											message:
												"Search calls by caller name, phone, or agent...",
										})}
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										{...getContentFieldProps(
											searchQuery,
											language === "he" ? "rtl" : "ltr",
											"ps-10",
										)}
									/>
								</div>

								{/* Filters Container */}
								<div className="flex flex-wrap items-center gap-2 w-full md:w-auto md:justify-end">
									{/* Date Range Filter */}
									<Popover
										open={datePickerOpen}
										onOpenChange={(open) => {
											if (open) setDraftDateRange(dateRange);
											setDatePickerOpen(open);
										}}
									>
										<PopoverTrigger asChild>
											<Button
												variant="outline"
												className="flex-1 sm:flex-none sm:min-w-[160px] flex items-center justify-start gap-2"
												aria-label={dateRangeLabel}
											>
												<Calendar className="w-4 h-4 shrink-0" />
												<span className="truncate text-xs sm:text-sm">
													{dateRangeLabel}
												</span>
											</Button>
										</PopoverTrigger>
										<PopoverContent
											className="w-auto max-w-[calc(100vw-2rem)] max-h-[80vh] overflow-y-auto p-0"
											align="end"
										>
											<div className="flex flex-wrap gap-2 border-b border-border p-3">
												{[
													{
														days: 1,
														label: t({
															id: "calls.dates.today",
															message: "Today",
														}),
													},
													{
														days: 7,
														label: t({
															id: "calls.dates.last7",
															message: "Last 7 days",
														}),
													},
													{
														days: 30,
														label: t({
															id: "dashboard.last30Days",
															message: "Last 30 days",
														}),
													},
													{
														days: 90,
														label: t({
															id: "calls.dates.last90",
															message: "Last 90 days",
														}),
													},
												].map(({ days, label }) => {
													const range = recentCallsRange(days);
													const selected =
														draftDateRange.from?.getTime() ===
															range.from.getTime() &&
														draftDateRange.to?.getTime() === range.to.getTime();
													return (
														<Button
															key={days}
															size="sm"
															variant={selected ? "secondary" : "ghost"}
															aria-pressed={selected}
															onClick={() => setDraftDateRange(range)}
														>
															{label}
														</Button>
													);
												})}
											</div>
											<div className="p-3 space-y-3 landscape:grid landscape:grid-cols-2 landscape:gap-3 landscape:space-y-0">
												<div>
													<label className="text-xs mb-1.5 block">
														{t({ id: "startDate", message: "Start Date" })}
													</label>
													<CalendarComponent
														className="p-2"
														classNames={compactDatePickerClassNames}
														mode="single"
														key={`from-${draftDateRange.from?.getTime()}`}
														defaultMonth={draftDateRange.from}
														selected={draftDateRange.from}
														onSelect={(date) =>
															setDraftDateRange((prev) => ({
																...prev,
																from: date || undefined,
																to:
																	date && prev.to && date > prev.to
																		? date
																		: prev.to,
															}))
														}
														initialFocus
													/>
												</div>
												<div>
													<label className="text-xs mb-1.5 block">
														{t({ id: "endDate", message: "End Date" })}
													</label>
													<CalendarComponent
														className="p-2"
														classNames={compactDatePickerClassNames}
														mode="single"
														key={`to-${draftDateRange.to?.getTime()}`}
														defaultMonth={draftDateRange.to}
														selected={draftDateRange.to}
														onSelect={(date) =>
															setDraftDateRange((prev) => ({
																...prev,
																to: date || undefined,
															}))
														}
														disabled={(date) =>
															draftDateRange.from
																? date < draftDateRange.from
																: false
														}
													/>
												</div>
											</div>
											<div className="flex justify-end gap-2 border-t border-border p-3">
												<Button
													variant="ghost"
													onClick={() => {
														setDraftDateRange({});
														clearDateFilter();
														setDatePickerOpen(false);
													}}
												>
													{t({ id: "calls.dates.clear", message: "Clear" })}
												</Button>
												<Button
													onClick={() => {
														setDateRange(draftDateRange);
														setDatePickerOpen(false);
													}}
												>
													{t({ id: "calls.dates.apply", message: "Apply" })}
												</Button>
											</div>
										</PopoverContent>
									</Popover>

									<Select
										value={directionFilter}
										onValueChange={(value) =>
											setDirectionFilter(
												value as "any" | "inbound" | "outbound" | "unknown",
											)
										}
									>
										<SelectTrigger className="w-full sm:w-[130px] flex-1 sm:flex-none">
											<SelectValue
												placeholder={t({
													id: "direction",
													message: "Direction",
												})}
											/>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="any">
												{t({
													id: "calls.direction.any",
													message: "Any direction",
												})}
											</SelectItem>
											<SelectItem value="inbound">
												{t({ id: "inbound", message: "Inbound" })}
											</SelectItem>
											<SelectItem value="outbound">
												{t({ id: "outbound", message: "Outbound" })}
											</SelectItem>
											<SelectItem value="unknown">
												{t({
													id: "calls.direction.unknown",
													message: "Unknown",
												})}
											</SelectItem>
										</SelectContent>
									</Select>

									<Select
										value={playbookFilter}
										onValueChange={setPlaybookFilter}
									>
										<SelectTrigger className="w-full sm:w-[180px] flex-1 sm:flex-none">
											<SelectValue
												placeholder={t({
													id: "playbook",
													message: "Playbook",
												})}
											/>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="all">
												{t({ id: "allPlaybooks", message: "All playbooks" })}
											</SelectItem>
											{playbookFilterOptions.map((playbook) => (
												<SelectItem key={playbook.id} value={playbook.id}>
													{playbook.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>

									<DropdownMenu>
										<DropdownMenuTrigger asChild>
											<Button
												variant="outline"
												className="flex items-center gap-2 shrink-0"
												aria-label={t({
													id: "filterCalls",
													message: "Filters",
												})}
											>
												<Filter className="w-4 h-4" />
												<span className="hidden sm:inline">
													{t({ id: "filterCalls", message: "Filters" })}
												</span>
												{hideFilterCount > 0 ? (
													<Badge
														variant="secondary"
														className="min-w-5 justify-center px-1.5 tabular-nums"
													>
														{hideFilterCount}
													</Badge>
												) : null}
											</Button>
										</DropdownMenuTrigger>
										<DropdownMenuContent align="end" className="w-56">
											<DropdownMenuCheckboxItem
												checked={hideEmptyCalls}
												onCheckedChange={setHideEmptyCalls}
											>
												{t({
													id: "hideEmptyCalls",
													message: "Hide Empty Calls",
												})}
											</DropdownMenuCheckboxItem>
											<DropdownMenuCheckboxItem
												checked={hideArchived}
												onCheckedChange={setHideArchived}
											>
												{t({
													id: "hideArchived",
													message: "Hide Archived Calls",
												})}
											</DropdownMenuCheckboxItem>
											<DropdownMenuCheckboxItem
												checked={hideTestCalls}
												onCheckedChange={setHideTestCalls}
											>
												{t({ id: "hideTestCalls", message: "Hide Test Calls" })}
											</DropdownMenuCheckboxItem>
											<DropdownMenuCheckboxItem
												checked={hideBlockedCalls}
												onCheckedChange={setHideBlockedCalls}
											>
												{t({
													id: "hideBlockedCalls",
													message: "Hide Blocked Calls",
												})}
											</DropdownMenuCheckboxItem>
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
							</div>
						</CardContent>
					</Card>

					{/* Calls Table */}
					<Card>
						<CardHeader>
							<CardTitle>{t({ id: "callsList", message: "Calls" })}</CardTitle>
							<CardDescription>
								{formatCallsCount(
									i18n,
									startIndex,
									endIndex,
									filteredCalls.length,
								)}
								{" · "}
								{periodLabel}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="space-y-4" data-testid="calls-list">
								{callsLoading ? (
									<div className="space-y-3" aria-busy="true">
										{Array.from({ length: 5 }, (_, index) => (
											<Skeleton key={index} className="h-24 w-full" />
										))}
									</div>
								) : callsError ? (
									<ContentState
										icon={<AlertTriangle className="h-5 w-5" />}
										title={t({
											id: "callsLoadFailed",
											message: "Calls could not be loaded",
										})}
										description={t({
											id: "callsLoadFailedDescription",
											message:
												"Check your connection and refresh the page to try again.",
										})}
										action={
											<Button
												variant="outline"
												onClick={() => window.location.reload()}
											>
												{t({ id: "retry", message: "Retry" })}
											</Button>
										}
									/>
								) : paginatedCalls.length === 0 && calls.length > 0 ? (
									<ContentState
										icon={<Filter className="h-5 w-5" />}
										title={t({
											id: "callsFilteredEmptyTitle",
											message: "No calls match these filters",
										})}
										description={t({
											id: "callsFilteredEmptyDescription",
											message:
												"Clear the filters to return to the full calls list.",
										})}
										action={
											<Button variant="outline" onClick={clearAllFilters}>
												{t({ id: "clearAllFilters", message: "Clear all" })}
											</Button>
										}
									/>
								) : paginatedCalls.length === 0 ? (
									<ContentState
										icon={<Phone className="h-5 w-5" />}
										title={t({
											id: "callsEmptyTitle",
											message: "No calls yet",
										})}
										description={t({
											id: "callsEmptyDescription",
											message:
												"Calls will appear here after one of your agents handles an interaction.",
										})}
									/>
								) : (
									paginatedCalls.map((call) => (
										<CallRecord
											key={call.id}
											call={call}
											onClick={() => onCallSelect?.(call.id)}
											onPlay={(e) => {
												e.stopPropagation();
												handlePlayAudio(call);
											}}
											onArchive={(e) => {
												e.stopPropagation();
												handleToggleArchive(call.id, call.interactionStatus);
											}}
											onMarkRead={(e) => {
												e.stopPropagation();
												void markRead(call.id, true);
											}}
											onMarkUnread={(e) => {
												e.stopPropagation();
												markRead(call.id, false);
											}}
											language={language}
											isAdmin={isAdmin}
											getAgentCompany={getAgentCompany}
											outboundContext={outboundContextByCallId.get(call.id)}
										/>
									))
								)}
							</div>

							{/* Pagination */}
							{totalPages > 1 && (
								<div className="mt-6">
									<Pagination>
										<PaginationContent>
											<PaginationItem>
												<PaginationPrevious
													onClick={() =>
														setCurrentPage((p) => Math.max(1, p - 1))
													}
													className={
														currentPage === 1
															? "pointer-events-none opacity-50"
															: "cursor-pointer"
													}
												/>
											</PaginationItem>

											{Array.from({ length: totalPages }, (_, i) => i + 1)
												.filter((page) => {
													// Show first page, last page, current page, and pages around current
													if (page === 1 || page === totalPages) return true;
													if (Math.abs(page - currentPage) <= 1) return true;
													return false;
												})
												.map((page, index, array) => (
													<Fragment key={page}>
														{/* Add ellipsis if there's a gap */}
														{index > 0 && array[index - 1] !== page - 1 && (
															<PaginationItem key={`ellipsis-${page}`}>
																<PaginationEllipsis />
															</PaginationItem>
														)}
														<PaginationItem>
															<PaginationLink
																onClick={() => setCurrentPage(page)}
																isActive={currentPage === page}
																className="cursor-pointer"
															>
																{page}
															</PaginationLink>
														</PaginationItem>
													</Fragment>
												))}

											<PaginationItem>
												<PaginationNext
													onClick={() =>
														setCurrentPage((p) => Math.min(totalPages, p + 1))
													}
													className={
														currentPage === totalPages
															? "pointer-events-none opacity-50"
															: "cursor-pointer"
													}
												/>
											</PaginationItem>
										</PaginationContent>
									</Pagination>
								</div>
							)}
						</CardContent>
					</Card>
				</>
			)}

			{/* Audio Player Dialog */}
			<Dialog
				open={!!audioUrl}
				onOpenChange={(open) => {
					if (!open) {
						setAudioUrl(null);
					}
				}}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{t({ id: "playRecording", message: "Play Recording" })}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="flex items-center justify-center p-6 bg-secondary rounded-lg">
							{audioUrl && (
								<audio
									ref={audioRef}
									controls
									autoPlay
									className="w-full"
									src={audioUrl}
									onRateChange={(e) =>
										setPlaybackRate(e.currentTarget.playbackRate)
									}
								>
									Your browser does not support the audio element.
								</audio>
							)}
						</div>
						<div className="flex justify-center gap-2">
							{[1, 1.5, 2].map((rate) => (
								<Button
									key={rate}
									variant={playbackRate === rate ? "default" : "outline"}
									size="sm"
									onClick={() => {
										if (audioRef.current) {
											audioRef.current.playbackRate = rate;
										}
									}}
								>
									{rate}x
								</Button>
							))}
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
